import { useEffect, useState } from 'react'

import { BRAND_NAME } from '@shared/contants/app'

const SBP_BANKS_KEY = 'sbp_banks'
const CACHE_DURATION = 1000 * 60 * 60 * 24 // 24 hours

const FAST_BANKS_NAMES = [
  'Сбербанк',
  'Т-Банк',
  'АЛЬФА-БАНК',
  'Банк ВТБ',
  'Озон Банк (Ozon)',
  'Банк ПСБ',
  'Газпромбанк',
  'Совкомбанк',
  'Райффайзенбанк',
  'Банк Венец',
] as const

export interface Bank {
  name: string
  logo: string
  url?: string
  schema?: string
}

interface BanksData {
  allBanks: Bank[]
  fastBanks: Bank[]
  staticBanks: Bank[]
  updatedAt?: number
}

interface RemoteBankMember {
  name: string
  logo: string
  url: string
}

interface RemoteBanksResponse {
  members?: RemoteBankMember[]
}

interface StaticBankEntry {
  bankName: string
  logoURL: string
  schema: string
}

interface StaticBanksResponse {
  dictionary?: StaticBankEntry[]
}

interface UseSBPBanksResult {
  banks: Bank[]
  fastBanks: Bank[]
  staticBanks: Bank[]
  loading: boolean
}

const saveBanksLocal = ({
  fastBanks = [],
  allBanks = [],
  staticBanks = [],
}: Partial<BanksData>): void => {
  if (allBanks && allBanks.length > 0) {
    try {
      localStorage.setItem(
        SBP_BANKS_KEY,
        JSON.stringify({
          allBanks,
          fastBanks,
          staticBanks,
          updatedAt: Date.now(),
        }),
      )
    }
    catch (error) {
      console.error('Error saving banks to local storage:', error)
    }
  }
  else {
    try {
      localStorage.removeItem(SBP_BANKS_KEY)
    }
    catch (error) {
      console.error('Error removing banks from local storage:', error)
    }
  }
}

const getBanksLocal = (): BanksData => {
  try {
    const banks = localStorage.getItem(SBP_BANKS_KEY)
    return banks
      ? (JSON.parse(banks) as BanksData)
      : {
        allBanks: [],
        fastBanks: [],
        staticBanks: [],
      }
  }
  catch (error) {
    console.error('Error retrieving banks from local storage:', error)
    return {
      allBanks: [],
      fastBanks: [],
      staticBanks: [],
    }
  }
}

const fetchRemoteBanks = async (): Promise<BanksData> => {
  try {
    const banksBlob = await fetch('https://widget.cbrpay.ru/v1/members/sub', {
      headers: {
        'X-CLIENT': BRAND_NAME,
        'X-PLATFORM': 'desktop',
      },
    })
    const banksRsp: RemoteBanksResponse = await banksBlob.json()

    if (banksRsp?.members) {
      const allBanks: Bank[] = banksRsp.members.map((bank) => ({
        name: bank.name,
        logo: bank.logo,
        url: bank.url,
      }))

      const fastBanks: Bank[] = FAST_BANKS_NAMES.map((name) =>
        allBanks.find((bank) => bank.name === name),
      ).filter((bank): bank is Bank => Boolean(bank))

      let staticBanks: Bank[] = []

      try {
        const staticBanksBlob = await fetch('https://qr.nspk.ru/proxyapp/c2bmembers.json')
        const staticBanksRsp: StaticBanksResponse = await staticBanksBlob.json()
        if (staticBanksRsp?.dictionary) {
          staticBanks = staticBanksRsp.dictionary
            .map((staticBank) => {
              // Находим банк из allBanks по имени, чтобы взять рабочий logo
              const bankWithLogo = allBanks.find((b) => b.name === staticBank.bankName)
              if (!bankWithLogo) return null // Пропускаем банки без логотипа
              return {
                name: staticBank.bankName,
                logo: bankWithLogo.logo,
                schema: staticBank.schema,
              }
            })
            .filter(b => b !== null) as Bank[]
        }
      }
      catch (error) {
        console.error('Error fetching static banks:', error)
      }

      saveBanksLocal({
        allBanks,
        fastBanks,
        staticBanks,
      })

      return {
        allBanks,
        fastBanks,
        staticBanks,
      }
    }
  }
  catch (error) {
    console.error('Error fetching remote banks:', error)
  }

  // Fallback - основные банки вручную
  const fallbackBanks: Bank[] = [
    {
      name: 'Сбербанк',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000111.png',
      schema: 'bank100000000111',
    },
    {
      name: 'Т-Банк',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000004.png',
      schema: 'bank100000000004',
    },
    {
      name: 'АЛЬФА-БАНК',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000008.png',
      schema: 'bank100000000008',
    },
    {
      name: 'Банк ВТБ',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000005.png',
      schema: 'bank100000000005',
    },
    {
      name: 'Газпромбанк',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000001.png',
      schema: 'bank100000000001',
    },
    {
      name: 'Райффайзенбанк',
      logo: 'https://qr.nspk.ru/proxyapp/logo/bank100000000007.png',
      schema: 'bank100000000007',
    },
  ]

  return {
    allBanks: fallbackBanks,
    fastBanks: fallbackBanks,
    staticBanks: fallbackBanks,
  }
}

export default function useSBPBanks (): UseSBPBanksResult {
  const [ banks, setBanks ] = useState<BanksData>({
    allBanks: [],
    fastBanks: [],
    staticBanks: [],
  })
  const [ loading, setLoading ] = useState(true)

  useEffect(() => {
    const fetchBanks = async () => {
      setLoading(true)
      try {
        const localBanks = getBanksLocal()
        if (
          localBanks?.allBanks &&
          localBanks?.allBanks?.length > 0 &&
          localBanks?.updatedAt &&
          localBanks.updatedAt > Date.now() - CACHE_DURATION
        ) {
          setBanks(localBanks)
        }
        else {
          const remoteBanks = await fetchRemoteBanks()
          if (remoteBanks?.allBanks && remoteBanks?.allBanks?.length > 0) {
            setBanks(remoteBanks)
          }
          else {
            console.warn('No banks found in remote source')
          }
        }
        setLoading(false)
      }
      catch (error) {
        setLoading(false)
        console.error('Error fetching banks:', error)
      }
    }
    fetchBanks()
  }, [])

  return {
    banks: banks.allBanks || [],
    fastBanks: banks.fastBanks || [],
    staticBanks: banks.staticBanks || [],
    loading,
  }
}
